import { ARRIVAL } from '../config/timing.js';
import { distanceM } from '../geo/distance.js';
import { FAHRBARE_ARTEN } from './types.js';
/**
 * Der nächste Treffpunkt und wann die Kolonne dort ist (Stufe 3, Schritt 2).
 *
 * Reine Rechnung, ohne Browser und ohne Datenbank — sie gehört nach Abschnitt
 * 5.2 des Leitfadens weder in eine React-Komponente noch in den Server. Der
 * Client hat alles dafür: die Route und die Treffpunkte aus `route_plan`, die
 * Führungsspur aus `leader_track_update`, die eigene Lage aus `locateOnRoute`.
 *
 * Sie serverseitig zu rechnen hieße, sie je Teilnehmer und je Sekunde erneut zu
 * rechnen und zu schicken — für eine Zahl, die sich aus bereits Übertragenem
 * ergibt.
 */
/**
 * Wie die Arten heißen — in der Planung und während der Fahrt dieselben Wörter.
 *
 * Hier und nicht zweimal in den Oberflächen: Hieße derselbe Punkt beim Planen
 * „Sammelpunkt" und beim Fahren „Treffpunkt", müsste der Organisator am Telefon
 * erklären, dass beides dasselbe ist. Kurz, weil sie während der Fahrt in eine
 * Zeile müssen — dieselbe Regel wie bei den Zuständen der Kolonne.
 */
export const WAYPOINT_KIND_NAMES = Object.freeze({
    start: 'Start',
    rally: 'Sammelpunkt',
    program: 'Programmpunkt',
    shaping: 'Stützpunkt',
    finish: 'Ziel',
});
/**
 * Was die Art bedeutet — ein Satz, für die Auswahl in der Planung.
 *
 * Während der Fahrt steht das nicht auf dem Bildschirm; dort ist kein Platz und
 * keine Zeit. Beim Planen entscheidet es darüber, ob jemand `rally` oder
 * `program` wählt, und davon hängt in Schritt 3 ab, ob eine Ankunft überhaupt
 * erfasst wird.
 */
export const WAYPOINT_KIND_HINTS = Object.freeze({
    start: 'Wo die Ausfahrt beginnt. Höchstens einer.',
    rally: 'Hier wird gewartet, bis alle da sind.',
    program: 'Mittagessen, Besichtigung — mit Uhrzeit und Verweildauer.',
    shaping: 'Formt nur die Strecke. Für den Fahrer unsichtbar.',
    finish: 'Wo die Ausfahrt endet. Höchstens einer.',
});
/**
 * Wie schnell diese Kolonne heute wirklich unterwegs ist, in Metern je Sekunde.
 *
 * Gemessen an der Führungsspur über die letzten `paceWindowM`. `null`, solange
 * zu wenig gefahren wurde — dann steht keine Zahl da statt einer geratenen.
 *
 * **Fahrzeit, nicht verstrichene Zeit.** `travelMs` wächst im Stillstand nicht;
 * die Begründung steht bei `ARRIVAL` in der Konfiguration.
 */
export function convoyPaceMps(track) {
    const kopf = track[track.length - 1];
    if (kopf === undefined) {
        return null;
    }
    /*
     * Rückwärts bis zum ersten Punkt, der weit genug zurückliegt. Liegt die ganze
     * Spur innerhalb des Fensters, gilt sie ganz — dann ist das Fenster eben
     * kürzer, und `paceMinM` entscheidet, ob das reicht.
     */
    let anfang = kopf;
    for (let i = track.length - 1; i >= 0; i -= 1) {
        const p = track[i];
        if (p === undefined) {
            continue;
        }
        anfang = p;
        if (kopf.distanceM - p.distanceM >= ARRIVAL.paceWindowM) {
            break;
        }
    }
    const strecke = kopf.distanceM - anfang.distanceM;
    const fahrzeitS = (kopf.travelMs - anfang.travelMs) / 1000;
    if (strecke < ARRIVAL.paceMinM || fahrzeitS <= 0) {
        return null;
    }
    return strecke / fahrzeitS;
}
/**
 * Der nächste Treffpunkt vor einem — oder `null`, wenn keiner mehr kommt.
 *
 * Stützpunkte (`shaping`) zählen nicht: Sie formen die Route, niemand hält
 * dort. Punkte ohne Lage auf der Route zählen ebenfalls nicht — ohne
 * `distanceM` ist „davor" keine beantwortbare Frage.
 */
export function nextWaypoint(waypoints, ownRouteDistanceM) {
    let bester = null;
    for (const w of waypoints) {
        if (w.distanceM === null || !FAHRBARE_ARTEN.includes(w.kind)) {
            continue;
        }
        if (w.distanceM <= ownRouteDistanceM) {
            continue;
        }
        if (bester === null || w.distanceM < (bester.distanceM ?? Infinity)) {
            bester = w;
        }
    }
    return bester;
}
/**
 * Der nächste Treffpunkt nach **Luftlinie** — für den Fall, dass das Fahrzeug
 * nicht auf der Route ist.
 *
 * „Der nächste entlang einer Strecke, auf der ich nicht fahre" ist keine
 * beantwortbare Frage. Der nächstgelegene, an dem ich noch nicht war, ist eine.
 */
function naechsterNachLuftlinie(waypoints, position, bereits) {
    let bester = null;
    for (const w of waypoints) {
        if (!FAHRBARE_ARTEN.includes(w.kind) || bereits.has(w.id)) {
            continue;
        }
        const meter = distanceM(position, w);
        if (bester === null || meter < bester.meter) {
            bester = { waypoint: w, meter };
        }
    }
    return bester;
}
/**
 * Was während der Fahrt über dem nächsten Treffpunkt steht.
 *
 * ## Zwei Maße, und die Anzeige sagt welches
 *
 * **Auf der Route** — höchstens `ARRIVAL.onRouteM` daneben und die Zuordnung
 * passt zum bisherigen Verlauf — gilt die Entfernung **entlang der Strecke**.
 * Sie ist die richtige: Die Kolonne fährt die Route, nicht die Luftlinie, und
 * bei einem Bogen sind das leicht drei Kilometer statt fünfhundert Metern.
 *
 * **Abseits** ist dieselbe Zahl wertlos. Das Lot auf eine Straße, auf der
 * niemand fährt, springt; am 16.09.2026 um 1,5 km in einem Schritt. Dann gilt
 * die **Luftlinie** zum nächstgelegenen Treffpunkt — wahr, nützlich auf der
 * Anfahrt, und als Luftlinie beschriftet.
 *
 * `null` heißt: Dazu lässt sich nichts sagen. Der Normalfall, wenn alle Punkte
 * schon erreicht sind.
 */
export function arrivalEstimate(eingabe) {
    const { ownRouteDistanceM, offRouteM, withinHint } = eingabe;
    const aufDerRoute = ownRouteDistanceM !== null &&
        offRouteM !== null &&
        offRouteM <= ARRIVAL.onRouteM &&
        withinHint;
    if (aufDerRoute) {
        const ziel = nextWaypoint(eingabe.waypoints, ownRouteDistanceM);
        /*
         * Hinter dem letzten Punkt steht **nichts** — und nicht etwa die Luftlinie
         * zu einem, an dem man vorbeigefahren ist. „Noch 2 km" zu einem Treffpunkt
         * hinter einem wäre schlimmer als gar nichts.
         */
        if (ziel === null || ziel.distanceM === null) {
            return null;
        }
        const remainingM = ziel.distanceM - ownRouteDistanceM;
        const pace = eingabe.paceMps;
        return {
            waypoint: ziel,
            remainingM,
            entlangDerRoute: true,
            travelS: pace === null || pace <= 0 ? null : Math.round(remainingM / pace),
        };
    }
    const nah = naechsterNachLuftlinie(eingabe.waypoints, eingabe.position, eingabe.bereits);
    if (nah === null) {
        return null;
    }
    return {
        waypoint: nah.waypoint,
        remainingM: nah.meter,
        entlangDerRoute: false,
        // Keine Zeit: Wie lange jemand für einen Weg braucht, den niemand kennt,
        // lässt sich nicht sagen.
        travelS: null,
    };
}
//# sourceMappingURL=waypoints.js.map